import { CommonModule } from '@angular/common';
import {
    Component,
    ElementRef,
    OnDestroy,
    OnInit,
    QueryList,
    ViewChildren,
} from '@angular/core';
import {
    FormArray,
    FormBuilder,
    FormControl,
    FormGroup,
    ReactiveFormsModule,
    Validators,
} from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { environment } from '../../../environments/environment';
import { RedirectHelperService } from '../../customidp/shared/services/redirect-helper.service';
import { CardFooterComponent } from '../card-footer/card-footer.component';
import { HeaderComponent } from '../header/header.component';
import { ApiResponse } from '../interfaces/api-response.interface';
import { EmployeeAuthService, OTPResponse } from '../services/employee-auth.service';
import { EmployerAuthService, EmployerOTPResponse } from '../services/employer-auth.service';
import { ErrorMessageService } from '../services/error-message.service';

type VerificationType = 'login' | 'signup' | 'password-reset' | 'employee-login' | 'employee-signup' | 'employee-password-reset' | 'employee-username-recovery' | 'salesforce-login';

@Component({
    selector: 'app-verify',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        CardFooterComponent,
        HeaderComponent,
        TranslateModule,
    ],
    templateUrl: './verify.component.html',
    styleUrls: ['./verify.component.css'],
})
export class VerifyComponent implements OnInit, OnDestroy {
    timer: number = 90;
    timerInterval: any;
    verificationError: string = '';
    successMessage: string = '';
    maskedEmail: string = '';
    showSuccessScreen: boolean = false;
    userPolicies: any[] = [];
    sessionToken: string = '';
    loading = false;
    otpForm: FormGroup;
    verificationType: VerificationType = 'login';
    userType: 'employer' | 'employee' | 'salesforce' = 'employer';
    title: string = '';
    primaryMessage: string = '';
    secondaryMessage: string = '';
    email: string = '';
    signupData: any = null;
    resetEmail: string = '';
    redirectCountdown: number = 3;
    countdownInterval: any;

    @ViewChildren('otpInput') otpInputs!: QueryList<ElementRef>;

    constructor(
        private readonly router: Router,
        private readonly route: ActivatedRoute,
        private readonly translate: TranslateService,
        private readonly titleService: Title,
        private readonly employerAuthService: EmployerAuthService,
        private readonly employeeAuthService: EmployeeAuthService,
        private readonly fb: FormBuilder,
        private readonly errorMessageService: ErrorMessageService,
        private readonly redirectHelper: RedirectHelperService
    ) {
        this.otpForm = this.fb.group({
            digits: this.fb.array(Array(6).fill('').map(() => new FormControl('', [Validators.required, Validators.pattern('[0-9]')]))),
        });
    }

    ngOnInit(): void {
        this.translate.use(this.translate.currentLang || 'en');
        this.translate.onLangChange.subscribe(() => {
            this.updateTitleAndMessages();
        });

        this.route.queryParams.subscribe(params => {
            const type = params['type'] as VerificationType;
            
            // Determine user type based on route path as well as query parameters
            const currentRoute = this.router.url;
            const isEmployerRoute = currentRoute.includes('/employer/');
            const isEmployeeRoute = currentRoute.includes('/employee/');
            const isSalesforceRoute = currentRoute.includes('/GroupSales/');

            if (isSalesforceRoute || type === 'salesforce-login') {
                this.userType = 'salesforce';
                this.verificationType = 'login';
            } else if (isEmployerRoute || type === 'login' || type === 'signup' || type === 'password-reset') {
                this.userType = 'employer';
                this.verificationType = type || 'login';
            } else if (isEmployeeRoute || type === 'employee-login' || type === 'employee-signup' || type === 'employee-password-reset' || type === 'employee-username-recovery') {
                this.userType = 'employee';
                this.verificationType = type || 'employee-login';
            } else {
                // Fallback: try to determine from type parameter
                const isEmployeeRoute = (type && typeof type === 'string' && (type as string).startsWith('employee-'));
                if (type === 'login' || type === 'signup' || type === 'password-reset') {
                    this.userType = 'employer';
                    this.verificationType = type;
                } else if (isEmployeeRoute) {
                    this.userType = 'employee';
                    this.verificationType = type;
                } else {
                    // Default fallback
                    this.userType = 'employer';
                    this.verificationType = 'login';
                }
            }

            this.initializeVerificationContext();
            this.setPageTitle();
            this.updateTitleAndMessages();
        });

        setTimeout(() => {
            this.otpInputs.first?.nativeElement.focus();
        });
    }

    private setPageTitle(): void {
        switch (this.verificationType) {
            case 'login':
                this.titleService.setTitle('Employer | Login Verification');
                break;
            case 'signup':
                this.titleService.setTitle('Employer | Registration Verification');
                break;
            case 'password-reset':
                this.titleService.setTitle('Employer | Password Reset Verification');
                break;
            case 'employee-login':
                this.titleService.setTitle('Employee | Login Verification');
                break;
            case 'employee-signup':
                this.titleService.setTitle('Employee | Registration Verification');
                break;
            case 'employee-password-reset':
                this.titleService.setTitle('Employee | Password Reset Verification');
                break;
            case 'employee-username-recovery':
                this.titleService.setTitle('Employee | Username Recovery Verification');
                break;
        }
    }

    private initializeVerificationContext(): void {
        switch (this.verificationType) {
            case 'login':
                this.initializeLoginVerification();
                break;
            case 'signup':
                this.initializeSignupVerification();
                break;
            case 'password-reset':
                this.initializePasswordResetVerification();
                break;
            case 'employee-login':
                this.initializeEmployeeLoginVerification();
                break;
            case 'employee-signup':
                this.initializeEmployeeSignupVerification();
                break;
            case 'employee-password-reset':
                this.initializeEmployeePasswordResetVerification();
                break;
            case 'employee-username-recovery':
                this.initializeEmployeeUsernameRecoveryVerification();
                break;
        }
    }

    private initializeLoginVerification(): void {
        const sessionDataString = sessionStorage.getItem('employerSessionData');

        if (!sessionDataString) {
            this.router.navigate(['/employer/account/login']);
            return;
        }

        try {
            const sessionData = JSON.parse(sessionDataString);
            this.sessionToken = sessionData.sessionId;
            this.maskedEmail = this.maskEmail(sessionData.email);
            this.userPolicies = sessionData.policies || [];
            this.startTimer();
        } catch (error) {
            console.error('Employer Login Verification - parse error:', error);
            this.router.navigate(['/employer/account/login']);
        }
    }

    private initializeSignupVerification(): void {
        const sessionDataString = sessionStorage.getItem('employerSessionData');
        console.log('Employer Signup Verification - sessionDataString:', sessionDataString);

        if (!sessionDataString) {
            this.router.navigate(['/employer/account/signup']);
            return;
        }

        try {
            const sessionData = JSON.parse(sessionDataString);
            console.log('Employer Signup Verification - parsed sessionData:', sessionData);
            this.sessionToken = sessionData.sessionId;
            console.log('Employer Signup Verification - sessionToken set to:', this.sessionToken);
            this.maskedEmail = this.maskEmail(sessionData.email);
            this.startTimer();
        } catch (error) {
            console.log('Employer Signup Verification - parse error:', error);
            this.router.navigate(['/employer/account/signup']);
        }
    }

    private initializePasswordResetVerification(): void {
        this.resetEmail = sessionStorage.getItem('resetEmail') || '';

        if (!this.resetEmail) {
            const employerDataString = sessionStorage.getItem('employerSessionData');
            if (employerDataString) {
                const employerData = JSON.parse(employerDataString);
                this.resetEmail = employerData.email || '';
            }
            if (!this.resetEmail) {
                this.router.navigate(['/employer/account/password']);
                return;
            }
        }

        this.maskedEmail = this.maskEmail(this.resetEmail);
        this.startTimer();
    }

    private initializeEmployeeLoginVerification(): void {
        const sessionDataString = sessionStorage.getItem('employeeSessionData');

        if (!sessionDataString) {
            this.router.navigate(['/employee/account/login']);
            return;
        }

        try {
            const sessionData = JSON.parse(sessionDataString);
            this.sessionToken = sessionData.sessionToken;
            this.email = sessionData.email;
            this.maskedEmail = this.maskEmail(sessionData.email);
            this.startTimer();
        } catch (error) {
            console.error('Employee Login Verification - parse error:', error);
            this.router.navigate(['/employee/account/login']);
        }
    }

    private initializeEmployeeSignupVerification(): void {
        const sessionDataString = sessionStorage.getItem('employeeSessionData');
        console.log('Employee Signup Verification - sessionDataString:', sessionDataString);

        if (!sessionDataString) {
            this.router.navigate(['/employee/account/signup']);
            return;
        }

        try {
            const sessionData = JSON.parse(sessionDataString);
            console.log('Employee Signup Verification - parsed sessionData:', sessionData);
            this.sessionToken = sessionData.sessionToken;
            console.log('Employee Signup Verification - sessionToken set to:', this.sessionToken);
            this.email = sessionData.email;
            this.maskedEmail = this.maskEmail(sessionData.email);
            this.startTimer();
        } catch (error) {
            console.log('Employee Signup Verification - parse error:', error);
            this.router.navigate(['/employee/account/signup']);
        }
    }

    private initializeEmployeePasswordResetVerification(): void {
        const sessionDataString = sessionStorage.getItem('employeeSessionData');

        if (!sessionDataString) {
            this.router.navigate(['/employee/account/password']);
            return;
        }

        try {
            const sessionData = JSON.parse(sessionDataString);
            this.sessionToken = sessionData.sessionToken;
            this.email = sessionData.email;
            this.maskedEmail = this.maskEmail(sessionData.email);
            this.startTimer();
        } catch (error) {
            console.error('Employee Password Reset Verification - parse error:', error);
            this.router.navigate(['/employee/account/password']);
        }
    }

    private initializeEmployeeUsernameRecoveryVerification(): void {
        const sessionDataString = sessionStorage.getItem('employeeSessionData');

        if (!sessionDataString) {
            this.router.navigate(['/employee/account/username']);
            return;
        }

        try {
            const sessionData = JSON.parse(sessionDataString);
            this.sessionToken = sessionData.sessionToken;
            this.email = sessionData.email || '';
            this.maskedEmail = this.maskEmail(this.email);
            this.startTimer();
        } catch (error) {
            console.error('Employee Username Recovery Verification - parse error:', error);
            this.router.navigate(['/employee/account/username']);
        }
    }

    private maskEmail(email: string): string {
        if (!email) {
            return '';
        }
        const [name, domain] = email.split('@');
        const { length: len } = name;
        const maskedName = name[0] + '...' + name[len - 1];
        return `${maskedName}@${domain}`;
    }

    ngOnDestroy(): void {
        this.clearCountdown();
        this.clearRedirectCountdown();
    }

    startTimer(): void {
        this.clearCountdown();
        this.timer = 90;
        this.timerInterval = setInterval(() => {
            if (this.timer > 0) {
                this.timer--;
            } else {
                this.clearCountdown();
            }
        }, 1000);
    }

    clearCountdown(): void {
        if (this.timerInterval) {
            clearInterval(this.timerInterval);
        }
    }

    startRedirectCountdown(callback: () => void, duration: number = 3000): void {
        this.clearRedirectCountdown();
        this.redirectCountdown = duration / 1000;
        this.countdownInterval = setInterval(() => {
            if (this.redirectCountdown > 1) {
                this.redirectCountdown--;
            } else {
                this.clearRedirectCountdown();
            }
        }, 1000);
        setTimeout(() => {
            this.clearRedirectCountdown();
            callback();
        }, duration);
    }

    clearRedirectCountdown(): void {
        if (this.countdownInterval) {
            clearInterval(this.countdownInterval);
        }
    }

    resendCode(): void {
        this.loading = true;
        this.verificationError = '';

        const authService = this.userType === 'employer' ? this.employerAuthService : this.employeeAuthService;

        authService.resendOTP(this.sessionToken).subscribe({
            next: () => {
                this.handleResendSuccess();
            },
            error: (err: any) => {
                if (err.error?.errorCode) {
                    this.errorMessageService.getErrorMessage(err.error.errorCode).subscribe(message => {
                        this.verificationError = message;
                    });
                } else {
                    this.translate.get('error.sendOtpFail').subscribe(message => {
                        this.verificationError = message;
                    });
                }
            },
            complete: () => {
                this.loading = false;
            }
        });
    }

    private handleResendSuccess(): void {
        this.verificationError = '';
        this.resetOtpForm();
        this.startTimer();
    }

    resetOtpForm(): void {
        this.otpForm.reset();
        if (this.otpInputs.first) {
            this.otpInputs.first.nativeElement.focus();
        }
    }

    onOtpInput(event: Event, idx: number): void {
        const target = event.target as HTMLInputElement;
        let value = target.value;

        if (isNaN(+value)) {
            this.otpForm.get('digits')?.get('' + idx)?.setValue('');
            return;
        }
        value = value.replace(/\D/g, '');

        if (value && idx < 5) {
            this.otpInputs.get(idx + 1)?.nativeElement.focus();
        }

        if (this.isOtpComplete()) {
            this.verifyOtp();
        }
    }

    onOtpKeydown(event: KeyboardEvent, idx: number): void {
        if (event.key === 'Backspace' && idx > 0 && !this.otpForm.get('digits')?.get('' + idx)?.value) {
            this.otpInputs.get(idx - 1)?.nativeElement.focus();
        }
    }

    onOtpPaste(event: ClipboardEvent): void {
        event.preventDefault();
        const pastedData = event.clipboardData?.getData('text').trim().slice(0, 6);
        if (pastedData && /^\d{6}$/.test(pastedData)) {
            const digits = this.otpForm.get('digits') as FormArray;
            pastedData.split('').forEach((char, index) => {
                digits.at(index).setValue(char);
            });
            this.otpInputs.last.nativeElement.focus();
            this.verifyOtp();
        }
    }

    isOtpComplete(): boolean {
        return this.otpForm.valid && this.otpForm.value.digits.every((d: string) => d !== '');
    }

    verifyOtp(): void {
        if (!this.isOtpComplete()) {
            this.translate.get('VERIFY.ERROR_INCOMPLETE_CODE').subscribe(message => {
                this.verificationError = message;
            });
            return;
        }

        if (!this.verificationType.includes('password-reset') && !this.sessionToken) {
            this.translate.get('VERIFY.ERROR_SESSION_EXPIRED').subscribe(message => {
                this.verificationError = message;
            });
            return;
        }

        this.loading = true;
        this.verificationError = '';
        const otp = this.otpForm.value.digits.join('');

        switch (this.verificationType) {
            case 'login':
                this.verifyLoginOtp(otp);
                break;
            case 'signup':
                this.verifySignupOtp(otp);
                break;
            case 'password-reset':
                this.verifyPasswordResetOtp(otp);
                break;
            case 'employee-login':
                this.verifyEmployeeLoginOtp(otp);
                break;
            case 'employee-signup':
                this.verifyEmployeeSignupOtp(otp);
                break;
            case 'employee-password-reset':
                this.verifyEmployeePasswordResetOtp(otp);
                break;
            case 'employee-username-recovery':
                this.verifyEmployeeUsernameRecoveryOtp(otp);
                break;
        }
    }

    private verifyLoginOtp(otp: string): void {
        const trxId = JSON.parse(sessionStorage.getItem('employerSessionData') || '{}').trxId;

        this.employerAuthService.submitOTP(this.sessionToken, otp, trxId).subscribe({
            next: (response: ApiResponse<EmployerOTPResponse>) => {
                if (this.errorMessageService.isSuccessResponse(response)) {
                    this.handleLoginSuccess(response.data, 'employer');
                } else {
                    this.errorMessageService.getErrorMessage(response.errorCode).subscribe(message => {
                        this.verificationError = message;
                    });
                    this.resetOtpForm();
                }
            },
            error: (err: any) => {
                if (err.error?.errorCode) {
                    this.errorMessageService.getErrorMessage(err.error.errorCode).subscribe(message => {
                        this.verificationError = message;
                    });
                } else {
                    this.translate.get('error.submitOtpFail').subscribe(message => {
                        this.verificationError = message;
                    });
                }
                this.resetOtpForm();
            },
            complete: () => {
                this.loading = false;
            },
        });
    }

    private handleLoginSuccess(data: any, userType: 'employer' | 'employee'): void {
        const defaultRole = userType === 'employer' ? 'N_HR' : 'N_ME';
        const selectedRole = data?.selectedRole || defaultRole;
        const authCode = data?.authCode;
        const defaultDashboard = userType === 'employer' ? '/employer/dashboard' : '/';

        if (environment.ENV_ACTIVE_PROFILE === environment.ON_CUSTOMIDP && authCode) {
            this.redirectHelper.redirectByRole(authCode, selectedRole);
        } else {
            this.router.navigate([defaultDashboard]);
        }
    }

    private verifySignupOtp(otp: string): void {
        const sessionDataKey = this.userType === 'employer' ? 'employerSessionData' : 'employeeSessionData';
        const trxId = JSON.parse(sessionStorage.getItem(sessionDataKey) || '{}').trxId;
        const authService = this.userType === 'employer' ? this.employerAuthService : this.employeeAuthService;
        const translationKey = this.userType === 'employer' ? 'EMPLOYER.VERIFY.SUCCESS_MESSAGE_SIGNUP' : 'EMPLOYEE.VERIFY.SUCCESS_MESSAGE_SIGNUP';
        const redirectPath = this.userType === 'employer' ? '/employer/account/login' : '/employee/account/login';

        authService.submitOTP(this.sessionToken, otp, trxId, true).subscribe({
            next: (response: any) => {
                if (this.errorMessageService.isSuccessResponse(response)) {
                    this.showSuccessScreen = true;
                    this.successMessage = this.translate.instant(translationKey);
                    this.startRedirectCountdown(() => {
                        this.router.navigate([redirectPath]);
                    }, 3000);
                } else {
                    this.errorMessageService.getErrorMessage(response.errorCode).subscribe(message => {
                        this.verificationError = message;
                    });
                    this.resetOtpForm();
                }
            },
            error: (err: any) => {
                if (err.error?.errorCode) {
                    this.errorMessageService.getErrorMessage(err.error.errorCode).subscribe(message => {
                        this.verificationError = message;
                    });
                } else {
                    this.translate.get('error.submitOtpFail').subscribe(message => {
                        this.verificationError = message;
                    });
                }
                this.resetOtpForm();
            },
            complete: () => {
                this.loading = false;
            }
        });
    }

    private verifyPasswordResetOtp(otp: string): void {
        const sessionDataString = sessionStorage.getItem('employerSessionData');
        let sessionId = '';
        if (sessionDataString) {
            const sessionData = JSON.parse(sessionDataString);
            sessionId = sessionData.sessionId;
        }
        this.employerAuthService.verifyForgotPasswordOTP(sessionId, otp).subscribe({
            next: (response: any) => {
                if (this.errorMessageService.isSuccessResponse(response)) {
                    this.showSuccessScreen = true;
                    this.successMessage = this.translate.instant('EMPLOYER.VERIFY.SUCCESS_MESSAGE_PASSWORD_RESET');
                    setTimeout(() => {
                        this.router.navigate(['/employer/account/change-password']);
                    }, 500);
                } else {
                    this.errorMessageService.getErrorMessage(response.errorCode).subscribe(message => {
                        this.verificationError = message;
                    });
                    this.resetOtpForm();
                }
            },
            error: (err: any) => {
                if (err.error?.errorCode) {
                    this.errorMessageService.getErrorMessage(err.error.errorCode).subscribe(message => {
                        this.verificationError = message;
                    });
                } else {
                    this.translate.get('error.submitOtpFail').subscribe(message => {
                        this.verificationError = message;
                    });
                }
                this.resetOtpForm();
            },
            complete: () => {
                this.loading = false;
            }
        });
    }

    private verifyEmployeeLoginOtp(otp: string): void {
        const trxId = JSON.parse(sessionStorage.getItem('employeeSessionData') || '{}').trxId;

        this.employeeAuthService.submitOTP(this.sessionToken, otp, trxId).subscribe({
            next: (response: ApiResponse<OTPResponse>) => {
                if (this.errorMessageService.isSuccessResponse(response)) {
                    this.handleLoginSuccess(response.data, 'employee');
                } else {
                    this.errorMessageService.getErrorMessage(response.errorCode).subscribe(message => {
                        this.verificationError = message;
                    });
                    this.resetOtpForm();
                }
            },
            error: (err: any) => {
                if (err.error?.errorCode) {
                    this.errorMessageService.getErrorMessage(err.error.errorCode).subscribe(message => {
                        this.verificationError = message;
                    });
                } else {
                    this.translate.get('error.submitOtpFail').subscribe(message => {
                        this.verificationError = message;
                    });
                }

                this.resetOtpForm();
            },
            complete: () => {
                this.loading = false;
            },
        });
    }

    private verifyEmployeeSignupOtp(otp: string): void {
        const trxId = JSON.parse(sessionStorage.getItem('employeeSessionData') || '{}').trxId;

        this.employeeAuthService.submitOTP(this.sessionToken, otp, trxId, true).subscribe({
            next: (response: any) => {
                if (this.errorMessageService.isSuccessResponse(response)) {
                    this.showSuccessScreen = true;
                    this.successMessage = this.translate.instant('EMPLOYEE.VERIFY.SUCCESS_MESSAGE_SIGNUP');
                    this.startRedirectCountdown(() => {
                        this.router.navigate(['/employee/account/login']);
                    }, 3000);
                } else {
                    this.errorMessageService.getErrorMessage(response.errorCode).subscribe(message => {
                        this.verificationError = message;
                    });
                    this.resetOtpForm();
                }
            },
            error: (err: any) => {
                if (err.error?.errorCode) {
                    this.errorMessageService.getErrorMessage(err.error.errorCode).subscribe(message => {
                        this.verificationError = message;
                    });
                } else {
                    this.translate.get('error.submitOtpFail').subscribe(message => {
                        this.verificationError = message;
                    });
                }
                this.resetOtpForm();
            },
            complete: () => {
                this.loading = false;
            },
        });
    }

    private verifyEmployeePasswordResetOtp(otp: string): void {
        const sessionDataString = sessionStorage.getItem('employeeSessionData');
        let sessionId = '';
        if (sessionDataString) {
            const sessionData = JSON.parse(sessionDataString);
            sessionId = sessionData.sessionId;
        }
        this.employeeAuthService.verifyForgotPasswordOTP(sessionId, otp).subscribe({
            next: (response: any) => {
                if (this.errorMessageService.isSuccessResponse(response)) {
                    this.showSuccessScreen = true;
                    this.successMessage = this.translate.instant('EMPLOYEE.VERIFY.SUCCESS_MESSAGE_PASSWORD_RESET');
                    setTimeout(() => {
                        this.router.navigate(['/employee/account/change-password']);
                    }, 500);
                } else {
                    this.errorMessageService.getErrorMessage(response.errorCode).subscribe(message => {
                        this.verificationError = message;
                    });
                    this.resetOtpForm();
                }
            },
            error: (err: any) => {
                if (err.error?.errorCode) {
                    this.errorMessageService.getErrorMessage(err.error.errorCode).subscribe(message => {
                        this.verificationError = message;
                    });
                } else {
                    this.translate.get('error.submitOtpFail').subscribe(message => {
                        this.verificationError = message;
                    });
                }
                this.resetOtpForm();
            },
            complete: () => {
                this.loading = false;
            }
        });
    }

    private verifyEmployeeUsernameRecoveryOtp(otp: string): void {
        const sessionDataString = sessionStorage.getItem('employeeSessionData');
        let sessionId = '';
        if (sessionDataString) {
            const sessionData = JSON.parse(sessionDataString);
            sessionId = sessionData.sessionId;
        }

        this.employeeAuthService.verifyForgotPasswordOTP(sessionId, otp).subscribe({
            next: (response: any) => {
                if (response.success) {
                    this.showSuccessScreen = true;
                } else {
                    this.errorMessageService.getErrorMessage(response.errorCode).subscribe(message => {
                        this.verificationError = message;
                    });
                    this.resetOtpForm();
                }
            },
            error: (err: any) => {
                if (err.error?.errorCode) {
                    this.errorMessageService.getErrorMessage(err.error.errorCode).subscribe(message => {
                        this.verificationError = message;
                    });
                } else {
                    this.translate.get('error.submitOtpFail').subscribe(message => {
                        this.verificationError = message;
                    });
                }

                this.resetOtpForm();
            },
            complete: () => {
                this.loading = false;
            },
        });
    }

    getSuccessTitleKey(): string {
        if (this.userType === 'employer') {
            return 'EMPLOYER.VERIFY.SUCCESS_TITLE';
        } else {
            return 'EMPLOYEE.VERIFY.SUCCESS_TITLE';
        }
    }

    getSuccessRedirectMessageKey(): string {
        if (this.verificationType === 'login') {
            return 'EMPLOYER.VERIFY.SUCCESS_MESSAGE_LOGIN';
        }
        if (this.verificationType === 'signup') {
            return 'EMPLOYER.VERIFY.SUCCESS_MESSAGE_SIGNUP';
        }
        if (this.verificationType === 'password-reset') {
            return 'EMPLOYER.VERIFY.SUCCESS_MESSAGE_PASSWORD_RESET';
        }
        if (this.verificationType.startsWith('employee-')) {
            return 'EMPLOYEE.VERIFY.SUCCESS_MESSAGE_REDIRECT';
        }
        return '';
    }

    getVerificationTitle(): string {
        if (this.showSuccessScreen) {
            return '';
        }
        switch (this.verificationType) {
            case 'login':
                return 'Employer Login';
            case 'signup':
                return 'Employer Registration';
            case 'password-reset':
                return 'Employer Password Reset';
            case 'employee-login':
                return 'Employee Login';
            case 'employee-signup':
                return 'Employee Registration';
            case 'employee-password-reset':
                return 'Employee Password Reset';
            case 'employee-username-recovery':
                return 'Employee Username Recovery';
            default:
                return 'Verification';
        }
    }

    getVerificationMessage(): string {
        if (this.showSuccessScreen) {
            return this.successMessage;
        }

        return this.translate.instant('VERIFY.OTP_SENT_TO_EMAIL', { maskedEmail: this.maskedEmail });
    }

    getPolicyStatusLabel(status: string): string {
        switch (status) {
            case '1':
                return 'Active';
            case '2':
                return 'Inactive';
            default:
                return 'Unknown';
        }
    }

    getPolicyTypeLabel(policyType: string): string {
        return policyType === '1' ? 'Group' : 'Individual';
    }

    getPolicyDisplayNumber(policy: any): string {
        return policy.policyDisplayNo;
    }

    get otpControls() {
        return (this.otpForm.get('digits') as FormArray).controls;
    }

    private updateTitleAndMessages(): void {
        this.translate.get('VERIFY.TITLE').subscribe((res: string) => {
            this.title = res;
        });

        this.translate.get('VERIFY.PRIMARY_MESSAGE').subscribe((res: string) => {
            this.primaryMessage = res;
        });

        if (this.userType === 'employer' && this.verificationType === 'login') {
            const policiesInfo = this.userPolicies
                .map(p => `Policy: ${p.policyDisplayNo} (${this.getPolicyTypeLabel(p.policyType)})`)
                .join(', ');
            this.secondaryMessage = `${policiesInfo}`;
        } else {
            this.translate.get('VERIFY.SECONDARY_MESSAGE').subscribe((res: string) => {
                this.secondaryMessage = res;
            });
        }
    }
} 